// frontend/src/pages/Web3WalletPage.jsx
import React, { useEffect, useState } from "react";
import { useWeb3 } from "../hooks/useWeb3";
import Web3ConnectButton from "../components/Web3ConnectButton";
import MultiAssetChart from "../components/MultiAssetChart";

/**
 * Page de gestion du portefeuille Web3 (connexion locale ou MetaMask)
 * ✅ Unifiée avec le hook useWeb3 et le service web3Service
 */
export default function Web3WalletPage() {
  const {
    address,
    balance,
    network,
    isConnected,
    isLoading,
    refreshNetwork,
    refreshBalance,

  } = useWeb3();

  const [showInfoModal, setShowInfoModal] = useState(false); // ✅ Info Modal State

  const [marketPrices, setMarketPrices] = useState(null);

  useEffect(() => {
    if (isConnected) {
      refreshNetwork();
      refreshBalance();
    }
  }, [isConnected]);

  // --- Récupération des prix du marché pour le graphique ---
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await fetch("/api/oracle/market-prices");
        const data = await res.json();

        // Transformer le tableau API [{symbol: "BTC", price_usd: ...}] en objet { BTC: 65000, ... }
        if (Array.isArray(data)) {
          const pricesMap = {};
          data.forEach((item) => {
            pricesMap[item.symbol] = item.price_usd;
          });
          setMarketPrices(pricesMap);
        }
      } catch (err) {
        console.error("Erreur chart wallet:", err);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 5000); // Mise à jour toutes les 5 sec
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-start py-12 text-white px-6">
      {/* Container Glassmorphism */}
      <div className="w-full max-w-6xl p-8 rounded-2xl border border-gray-700 shadow-2xl" style={{
        background: "rgba(17, 24, 39, 0.75)",
        backdropFilter: "blur(12px)"
      }}>
        <div style={{ background: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(5px)", padding: "20px", borderRadius: "12px", marginBottom: "20px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
            <h1 className="text-3xl font-bold text-center" style={{ margin: 0 }}>
              💼 Portefeuille Web3 — Oracle d’Entropie
            </h1>
            <button
              onClick={() => setShowInfoModal(true)}
              style={{
                background: "rgba(0, 255, 255, 0.1)", border: "1px solid #0ff", color: "#0ff",
                borderRadius: "50%", width: "24px", height: "24px", fontSize: "0.9rem",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
              }}
              title="Info Wallet"
            >
              i
            </button>
          </div>

          <p className="text-gray-400 text-center max-w-lg">
            Connectez votre portefeuille MetaMask ou utilisez le réseau local
            Hardhat pour interagir directement avec les contrats déployés.
          </p>
        </div>

        <Web3ConnectButton />

        {/* --- Section Graphique Financier --- */}
        <div className="w-full max-w-4xl mt-12 mb-8">
          <MultiAssetChart currentPrices={marketPrices} />
        </div>

        {isConnected && (
          <div className="bg-gray-800 p-4 rounded-xl shadow-md w-full max-w-md text-sm mt-4">
            <p className="mb-1">
              <span className="text-gray-400">Adresse :</span> {address}
            </p>
            <p className="mb-1">
              <span className="text-gray-400">Solde :</span>{" "}
              {Number(balance).toFixed(4)} ETH
            </p>
            <p>
              <span className="text-gray-400">Réseau :</span>{" "}
              {network?.name || "Inconnu"} (Chain ID {network?.chainId})
            </p>
          </div>
        )}

        {isLoading && (
          <p className="mt-6 text-gray-500 text-sm animate-pulse">
            Chargement des informations blockchain…
          </p>
        )}
      </div>

      {/* ✅ MODAL INFO WALLET */}
      {
        showInfoModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
            background: "rgba(0,0,0,0.8)", backdropFilter: "blur(5px)", zIndex: 9999,
            display: "flex", justifyContent: "center", alignItems: "center"
          }} onClick={() => setShowInfoModal(false)}>
            <div style={{
              background: "rgba(20, 30, 50, 0.95)", border: "1px solid #0ff", padding: "30px", borderRadius: "16px",
              maxWidth: "600px", width: "90%", boxShadow: "0 0 50px rgba(0, 255, 255, 0.2)",
              color: "#fff", position: "relative", maxHeight: "90vh", overflowY: "auto"
            }} onClick={e => e.stopPropagation()}>

              <h2 style={{ borderBottom: "1px solid #333", paddingBottom: "10px", marginBottom: "20px", color: "#0ff" }}>
                Connexion Sécurisée & Non-Custodial
              </h2>

              <div style={{ textAlign: "left", lineHeight: "1.6" }}>
                <h3 style={{ color: "#ffd369", marginTop: "15px" }}>1. Sécurité Totale</h3>
                <p style={{ color: "#ddd" }}>
                  Cette interface est en <strong>"Lecture Seule"</strong>. Nous ne stockons jamais vos clés privées. Vous restez le seul maître de vos actifs via votre extension (MetaMask, etc.).
                </p>

                <h3 style={{ color: "#ffd369", marginTop: "15px" }}>2. À quoi ça sert ?</h3>
                <p style={{ color: "#ddd" }}>
                  Connecter votre portefeuille permet de visualiser vos certificats QUID qui sont ancrés sur la Blockchain. C'est votre <strong>"Coffre-fort"</strong> numérique.
                </p>

                <h3 style={{ color: "#ffd369", marginTop: "15px" }}>3. Confidentialité</h3>
                <p style={{ color: "#ddd" }}>
                  Aucune donnée personnelle n'est liée à votre adresse publique sans votre consentement explicite (Signature).
                </p>
              </div>

              <button onClick={() => setShowInfoModal(false)} style={{
                marginTop: "25px", width: "100%", padding: "12px", background: "#333", color: "#fff",
                border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold"
              }}>
                Fermer
              </button>

            </div>
          </div>
        )
      }
    </div>
  );
}
